/**
 * Word search generation.
 *
 * Words are hidden inside a square grid of letters. Each word is placed along a
 * straight line — horizontal, vertical or (depending on the allowed directions)
 * diagonal and/or reversed — and may cross another word on a shared letter.
 * Every remaining cell is then filled with a random letter.
 *
 * Unlike a crossword, the words do not need to interlock, so there is no
 * connectivity requirement (see `canBuildCrossword` in `./crossword`).
 */
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
/** Across and down only — the simplest, easiest-to-read grid. */
export const DIRECTIONS_SIMPLE = [
    [0, 1], // →
    [1, 0], // ↓
];
/** All eight directions, including diagonals and reversed words. */
export const DIRECTIONS_ALL = [
    [0, 1], // →
    [1, 0], // ↓
    [1, 1], // ↘
    [-1, 1], // ↗
    [0, -1], // ←
    [-1, 0], // ↑
    [-1, -1], // ↖
    [1, -1], // ↙
];
/**
 * Normalise raw answers: uppercase, letters only, drop duplicates and any word
 * shorter than two letters.
 */
export function cleanWords(words) {
    const seen = new Set();
    const out = [];
    words.forEach((raw) => {
        const w = (raw || '').toUpperCase().replace(/[^A-Z]/g, '');
        if (w.length >= 2 && !seen.has(w)) {
            seen.add(w);
            out.push(w);
        }
    });
    return out;
}
/** Choose an initial grid size that comfortably fits the words plus filler. */
function pickSize(words) {
    const longest = words.reduce((m, w) => Math.max(m, w.length), 0);
    const letters = words.reduce((s, w) => s + w.length, 0);
    const byArea = Math.ceil(Math.sqrt(letters * 2.1));
    return Math.max(longest + 1, byArea, 10);
}
function emptyGrid(n) {
    return Array.from({ length: n }, () => new Array(n).fill(''));
}
/**
 * Try to drop `word` into the grid. Picks a random allowed direction and a
 * random in-bounds start, then accepts the path only if every cell is empty or
 * already holds the matching letter (a shared crossing). Retries up to
 * `maxAttempts` times. Mutates `grid` on success and returns the cell path.
 */
function tryPlace(grid, n, word, directions, maxAttempts) {
    let attempts = maxAttempts;
    while (attempts-- > 0) {
        const [dr, dc] = directions[Math.floor(Math.random() * directions.length)];
        // valid start range so the word stays in bounds in this direction
        const rMin = dr < 0 ? word.length - 1 : 0;
        const rMax = dr > 0 ? n - word.length : n - 1;
        const cMin = dc < 0 ? word.length - 1 : 0;
        const cMax = dc > 0 ? n - word.length : n - 1;
        if (rMax < rMin || cMax < cMin)
            continue;
        const sr = rMin + Math.floor(Math.random() * (rMax - rMin + 1));
        const sc = cMin + Math.floor(Math.random() * (cMax - cMin + 1));
        const cells = [];
        let ok = true;
        for (let i = 0; i < word.length; i++) {
            const rr = sr + dr * i;
            const cc = sc + dc * i;
            const cur = grid[rr][cc];
            if (cur !== '' && cur !== word[i]) {
                ok = false;
                break;
            }
            cells.push([rr, cc]);
        }
        if (!ok)
            continue;
        for (let i = 0; i < word.length; i++) {
            grid[cells[i][0]][cells[i][1]] = word[i];
        }
        return cells;
    }
    return null;
}
/**
 * Generate a word search from a list of answers. Returns `null` if there are no
 * usable words, or (extremely unlikely) if the words cannot be placed even
 * after growing the grid.
 */
export function generateWordSearch(rawWords, options = {}) {
    const words = cleanWords(rawWords);
    if (words.length === 0)
        return null;
    const directions = options.directions ?? DIRECTIONS_ALL;
    const maxGrow = options.maxGrow ?? 8;
    const maxAttempts = options.maxAttempts ?? 120;
    const baseSize = pickSize(words);
    // longest first so large words claim space before the grid fills up
    const ordered = [...words].sort((a, b) => b.length - a.length);
    for (let grow = 0; grow < maxGrow; grow++) {
        const n = baseSize + grow;
        const grid = emptyGrid(n);
        const placements = [];
        let placedAll = true;
        for (const word of ordered) {
            const cells = tryPlace(grid, n, word, directions, maxAttempts);
            if (!cells) {
                placedAll = false;
                break;
            }
            placements.push({ answer: word, cells });
        }
        if (!placedAll)
            continue;
        // fill remaining blanks with random letters
        const cells = [];
        for (let r = 0; r < n; r++) {
            const row = [];
            for (let c = 0; c < n; c++) {
                const isWord = grid[r][c] !== '';
                row.push({
                    letter: isWord
                        ? grid[r][c]
                        : ALPHABET[Math.floor(Math.random() * 26)],
                    isWord,
                });
            }
            cells.push(row);
        }
        return { cells, size: n, placements };
    }
    return null;
}
